import React, { useMemo } from 'react';

interface Props {
  v1Label?: string;
  v2Label?: string;
  v1Payload: any;
  v2Payload: any;
}

interface DiffRow {
  field: string;
  expected: unknown;
  actual: unknown;
  status: 'match' | 'mismatch' | 'missing' | 'extra' | 'both_null';
}

function computeDiff(expected: any, actual: any, prefix = ''): DiffRow[] {
  const rows: DiffRow[] = [];
  const isObj = (v: any) => v !== null && typeof v === 'object' && !Array.isArray(v);

  if (isObj(expected) || isObj(actual)) {
    const keys = new Set<string>([
      ...Object.keys(expected ?? {}),
      ...Object.keys(actual ?? {}),
    ]);
    for (const k of keys) {
      const fp = prefix ? `${prefix}.${k}` : k;
      rows.push(...computeDiff(expected?.[k], actual?.[k], fp));
    }
    return rows;
  }
  if (Array.isArray(expected) || Array.isArray(actual)) {
    const a = Array.isArray(expected) ? expected : [];
    const b = Array.isArray(actual) ? actual : [];
    const len = Math.max(a.length, b.length);
    for (let i = 0; i < len; i++) {
      rows.push(...computeDiff(a[i], b[i], `${prefix}[${i}]`));
    }
    return rows;
  }

  let status: DiffRow['status'] = 'match';
  const eMissing = expected === undefined || expected === null;
  const aMissing = actual === undefined || actual === null;
  if (eMissing && aMissing) status = 'both_null';
  else if (eMissing) status = 'extra';
  else if (aMissing) status = 'missing';
  else if (String(expected) !== String(actual)) status = 'mismatch';

  rows.push({ field: prefix || '(root)', expected, actual, status });
  return rows;
}

function fmt(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const CompareVersionsView: React.FC<Props> = ({
  v1Label = 'v1 (validée)',
  v2Label = 'v2 (en attente)',
  v1Payload,
  v2Payload,
}) => {
  const rows = useMemo(() => computeDiff(v1Payload, v2Payload), [v1Payload, v2Payload]);

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
        Comparaison {v1Label} vs {v2Label}
      </div>
      <table className="w-full text-sm" data-testid="compare-versions-table">
        <thead>
          <tr className="bg-gray-50 text-gray-600 text-xs">
            <th className="text-left px-4 py-2 w-1/3">Champ</th>
            <th className="text-left px-4 py-2 w-1/3">{v1Label}</th>
            <th className="text-left px-4 py-2 w-1/3">{v2Label}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.field}
              className={
                r.status === 'mismatch'
                  ? 'bg-yellow-50'
                  : r.status === 'missing'
                  ? 'bg-red-50'
                  : r.status === 'extra'
                  ? 'bg-green-50'
                  : ''
              }
            >
              <td className="px-4 py-1 font-mono text-xs text-gray-700">{r.field}</td>
              <td className="px-4 py-1 text-gray-800">{fmt(r.expected)}</td>
              <td className="px-4 py-1 text-gray-800">{fmt(r.actual)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompareVersionsView;
